import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { usePositionContext } from '../../contexts/PositionContext';
import { SEARCH_TOKENS } from '../../graphql/queries/tokens';
import { parseFloatFromString, getDefaultCurrency, formatCurrency } from '../../utils/formatters';
import AllocationLocationSelector from './AllocationLocationSelector';

const AddTokenModal = ({
  isOpen,
  onClose,
  onConfirm, // (tokenSelection, amountNumber, planId, targetLocationId) => void
  allocations,
  isEdit = false,
  presetTokenSelection = null,
  presetAmount = null,
  allowedTokenSelection = null,
  focusAmountOnOpen = false,
  showPlanSelect = false,
  planOptions = [], // [{id, name}]
  initialPlanId = null,
  persistAmount = false,
  persistAmountKey = 'allocation_last_amount',
  locations = [],
  presetTargetLocationId = '',
  onCreateTargetLocation = null,
  onRenameTargetLocation = null,
  onDeleteTargetLocation = null,
}) => {
  const currency = getDefaultCurrency();
  const DEFAULT_MODAL_AMOUNT = 100;
  const [amount, setAmount] = useState(String(DEFAULT_MODAL_AMOUNT));
  const hasInitializedAmountRef = useRef(false);
  const [selectedPlanId, setSelectedPlanId] = useState(initialPlanId || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [userNavigatedSuggestions, setUserNavigatedSuggestions] = useState(false);
  const inputRef = useRef(null);
  const amountRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const [targetLocationId, setTargetLocationId] = useState('');
  const [tokenFocused, setTokenFocused] = useState(false);
  const listContainerRef = useRef(null);
  const { positions, loading: positionsLoading } = usePositionContext();
  const [searchTokens] = useLazyQuery(SEARCH_TOKENS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'ignore'
  });

  // Load defaults on open (only when modal toggles open)
  useEffect(() => {
    if (!isOpen) return;
    // Prevent persisting during initial load pass
    hasInitializedAmountRef.current = false;
    try {
      // If persistence is enabled, prefer the saved value
      if (persistAmount) {
        const saved = localStorage.getItem(persistAmountKey);
        const parsed = parseFloatFromString(saved);
        if (!Number.isNaN(parsed) && parsed > 0) {
          setAmount(String(parsed));
        } else if (isEdit && (presetAmount !== null && presetAmount !== undefined)) {
          setAmount(String(presetAmount));
        } else {
          const fallbackSaved = localStorage.getItem('allocation_last_amount');
          const fallbackParsed = parseFloatFromString(fallbackSaved);
          setAmount(!Number.isNaN(fallbackParsed) && fallbackParsed > 0 ? String(fallbackParsed) : String(DEFAULT_MODAL_AMOUNT));
        }
      } else if (isEdit && (presetAmount !== null && presetAmount !== undefined)) {
        setAmount(String(presetAmount));
      } else {
        const saved = localStorage.getItem('allocation_last_amount');
        const parsed = parseFloatFromString(saved);
        setAmount(!Number.isNaN(parsed) && parsed > 0 ? String(parsed) : String(DEFAULT_MODAL_AMOUNT));
      }
    } catch (_) {
      setAmount(String(DEFAULT_MODAL_AMOUNT));
    }
    // Mark initialization complete so further changes can persist
    hasInitializedAmountRef.current = true;

    setTargetLocationId(presetTargetLocationId || '');

    // Token: prefill from preset when editing
    if (isEdit && presetTokenSelection && (presetTokenSelection.name || presetTokenSelection.tokenId || presetTokenSelection.coingeckoApiId)) {
      setSelectedToken({
        tokenId: presetTokenSelection.tokenId || '',
        coingeckoApiId: presetTokenSelection.coingeckoApiId || '',
        coingeckoSlug: null,
        name: presetTokenSelection.name || '',
        ticker: presetTokenSelection.ticker || '',
      });
      setSearchTerm(presetTokenSelection.name || '');
      setFilteredResults([]);
      setSelectedIndex(0);
    } else {
      setSearchTerm('');
      setFilteredResults([]);
      setSelectedIndex(0);
      setUserNavigatedSuggestions(false);
      setSelectedToken(null);
    }
  // We intentionally avoid including dependencies to prevent re-runs that cause flicker.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Initialize selected plan when opening or when options change
  useEffect(() => {
    if (!isOpen) return;
    if (initialPlanId) {
      setSelectedPlanId(initialPlanId);
    } else if ((planOptions || []).length > 0) {
      setSelectedPlanId(planOptions[0].id);
    } else {
      setSelectedPlanId(null);
    }
  }, [isOpen, initialPlanId, planOptions]);

  // Focus appropriate field when opening
  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => {
      if ((isEdit || focusAmountOnOpen) && amountRef.current) {
        amountRef.current.focus();
        amountRef.current.select?.();
      } else if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 10);
  }, [isOpen, isEdit, focusAmountOnOpen]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    if (isOpen) {
      window.addEventListener('keydown', onKey);
    }
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Persist amount locally on change
  useEffect(() => {
    if (!persistAmount && isEdit) return;
    if (!hasInitializedAmountRef.current) return; // skip persisting the initial default before load
    try {
      if (amount && parseFloatFromString(amount) > 0) {
        const key = persistAmount ? persistAmountKey : 'allocation_last_amount';
        localStorage.setItem(key, String(amount));
        // Persist silently
      }
    } catch (_) {}
  // We intentionally avoid including dependencies to prevent extra writes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount]);

  const existingTokenIds = useMemo(() => {
    const ids = (allocations || []).filter(a => a.token?.id).map(a => a.token.id);
    const allowedId = allowedTokenSelection?.tokenId || null;
    return allowedId ? ids.filter(id => id !== allowedId) : ids;
  }, [allocations, allowedTokenSelection?.tokenId]);

  const existingCoingeckoApiIds = useMemo(() => {
    const ids = (allocations || []).filter(a => a.token?.coingeckoApiId).map(a => a.token.coingeckoApiId);
    const allowedApi = allowedTokenSelection?.coingeckoApiId || null;
    return allowedApi ? ids.filter(id => id !== allowedApi) : ids;
  }, [allocations, allowedTokenSelection?.coingeckoApiId]);

  // Build a result item from a token and optional position
  const createResultItem = useCallback((token, position = null, isFromSearch = false) => {
    return {
      id: token.id || `search-${token.coingeckoApiId}`,
      name: token.name,
      ticker: token.ticker,
      coingeckoApiId: token.coingeckoApiId || null,
      tokenId: token.id || null,
      hasPosition: !!position,
      isFromSearch,
      holdingsAmount: position?.holdingsAmount || null,
      holdingsValue: position?.holdingsValue || null,
      roi: position?.current?.roi || null,
      locationBalances: Array.isArray(position?.locationBalances) ? position.locationBalances : [],
      marketCapRank: token.marketCapRank || null,
    };
  }, []);

  const buildSelectedToken = (result) => ({
    tokenId: result.tokenId || '',
    coingeckoApiId: result.coingeckoApiId || '',
    coingeckoSlug: null,
    name: result.name,
    ticker: result.ticker,
    hasPosition: !!result.hasPosition,
    holdingsAmount: result.holdingsAmount || null,
    holdingsValue: result.holdingsValue || null,
    locationBalances: Array.isArray(result.locationBalances) ? result.locationBalances : [],
  });

  // Locally sort/filter existing positions
  const sortedPositions = useCallback(() => {
    if (!positions) return [];
    return [...positions]
      .filter(p => p.token)
      .sort((a, b) => {
        const aValue = parseFloat((a.holdingsValue || '0').toString().replace(/[$,]/g, ''));
        const bValue = parseFloat((b.holdingsValue || '0').toString().replace(/[$,]/g, ''));
        return bValue - aValue;
      })
      .map(position => createResultItem(position.token, position));
  }, [positions, createResultItem]);

  const filterPositions = useCallback((term) => {
    const sorted = sortedPositions();
    if (!term) return sorted;
    const lower = term.toLowerCase();
    return sorted.filter(item =>
      item.name.toLowerCase().includes(lower) ||
      item.ticker.toLowerCase().includes(lower)
    );
  }, [sortedPositions]);

  // Debounced API search
  const performApiSearch = useCallback(async (term) => {
    if (term.length <= 2) return [];
    try {
      const result = await searchTokens({ variables: { query: term } });
      if (result.data?.searchTokens) {
        return result.data.searchTokens.map(token => ({
          id: token.tokenId,
          name: token.name,
          ticker: token.ticker,
          coingeckoApiId: token.coingeckoApiId,
          tokenId: token.tokenId,
          marketCapRank: token.marketCapRank,
        })).map(t => createResultItem(t, null, true));
      }
    } catch (_) {}
    return [];
  }, [searchTokens, createResultItem]);

  // Main search effect
  useEffect(() => {
    if (!isOpen) return;
    const process = async () => {
      const local = filterPositions(searchTerm);
      if (searchTerm.length <= 2) {
        // Filter out duplicates already in allocations
        const deduped = local.filter(r => (
          (!r.tokenId || !existingTokenIds.includes(r.tokenId)) &&
          (!r.coingeckoApiId || !existingCoingeckoApiIds.includes(r.coingeckoApiId))
        ));
        setFilteredResults(deduped);
        setSelectedIndex(0);
        setUserNavigatedSuggestions(false);
        return;
      }
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(async () => {
        const apiRes = await performApiSearch(searchTerm);
        // Deduplicate by tokenId/coingeckoApiId
        const existingIds = new Set(local.map(r => r.tokenId).filter(Boolean));
        const existingApis = new Set(local.map(r => r.coingeckoApiId).filter(Boolean));
        const apiFiltered = apiRes.filter(r => (!r.tokenId || !existingIds.has(r.tokenId)) && (!r.coingeckoApiId || !existingApis.has(r.coingeckoApiId)));
        const merged = [...local, ...apiFiltered].filter(r => (
          (!r.tokenId || !existingTokenIds.includes(r.tokenId)) &&
          (!r.coingeckoApiId || !existingCoingeckoApiIds.includes(r.coingeckoApiId))
        ));
        setFilteredResults(merged);
        setSelectedIndex(0);
        setUserNavigatedSuggestions(false);
      }, 500);
    };
    process();
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [isOpen, searchTerm, filterPositions, performApiSearch, existingTokenIds, existingCoingeckoApiIds]);

  // Auto-scroll highlighted suggestion into view
  useEffect(() => {
    if (!tokenFocused) return;
    const container = listContainerRef.current;
    if (!container) return;
    const el = container.querySelector(`[data-idx="${selectedIndex}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, tokenFocused]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredResults.length - 1));
      setUserNavigatedSuggestions(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
      setUserNavigatedSuggestions(true);
    } else if (e.key === 'Enter' && filteredResults.length > 0) {
      const selected = filteredResults[selectedIndex];
      if (selected) {
        setSelectedToken(buildSelectedToken(selected));
        setSearchTerm(selected.name);
        setFilteredResults([]);
        setTimeout(() => amountRef.current?.focus(), 0);
      }
    }
  };

  const handleSubmit = () => {
    const amt = parseFloatFromString(amount);
    if (!selectedToken || Number.isNaN(amt) || amt < 0) return;
    onConfirm?.(selectedToken, amt, selectedPlanId, targetLocationId || '');
  };

  // Numeric input helpers (allow digits and single decimal separator)
  const numericKeyDown = (e) => {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End', 'Enter'];
    if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (/^[0-9]$/.test(e.key)) return;
    if (e.key === '.' || e.key === ',') {
      const v = e.currentTarget.value || '';
      if (v.includes('.') || v.includes(',')) {
        e.preventDefault();
      }
      return;
    }
    e.preventDefault();
  };

  const numericOnPaste = (e) => {
    try {
      const text = (e.clipboardData || window.clipboardData).getData('text');
      if (!/^\d*(?:[.,]\d*)?$/.test(text)) {
        e.preventDefault();
      }
    } catch (_) {}
  };

  const handleSelectByMouse = (r) => {
    setSelectedToken(buildSelectedToken(r));
    setSearchTerm(r.name);
    setFilteredResults([]);
    setTimeout(() => amountRef.current?.focus(), 0);
  };

  const onTokenInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (selectedToken) setSelectedToken(null);
    if (val.length === 0) setUserNavigatedSuggestions(false);
  };

  // Auto-select highlighted suggestion on blur if suggestions exist and nothing selected yet
  const handleTokenBlur = () => {
    // Only auto-select on blur if user typed something or navigated suggestions
    const hasTyped = searchTerm && searchTerm.trim().length > 0;
    if (!selectedToken && filteredResults.length > 0 && (hasTyped || userNavigatedSuggestions)) {
      const r = filteredResults[selectedIndex] || filteredResults[0];
      if (r) {
        setSelectedToken(buildSelectedToken(r));
        setSearchTerm(r.name);
        setFilteredResults([]);
      }
    }
    setTokenFocused(false);
  };

  if (!isOpen) return null;

  const headerText = isEdit ? 'Edit allocation' : 'Add allocation';
  const actionText = isEdit ? 'Save' : 'Add';
  const tokenSuggestionsVisible = tokenFocused;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{headerText}</h3>

        {showPlanSelect && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Allocation plan</label>
            {(planOptions || []).length > 0 ? (
              <select
                className="w-full p-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded"
                value={selectedPlanId || ''}
                onChange={(e) => setSelectedPlanId(e.target.value)}
              >
                {(planOptions || []).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            ) : (
              <div className="text-sm text-gray-600 dark:text-gray-300">No plans yet — a new plan will be created.</div>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Token</label>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                placeholder="Search token by name or ticker..."
                className="w-full p-2 pr-9 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded"
                value={searchTerm}
                onChange={onTokenInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setTokenFocused(true)}
                onBlur={handleTokenBlur}
              />
              {selectedToken && (
                <svg className="w-5 h-5 text-green-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </div>
          <div className="w-64">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount ({currency})</label>
            <input
              type="string"
              value={amount}
              ref={amountRef}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^\d*(?:[.,]\d*)?$/.test(v)) {
                  setAmount(v);
                }
              }}
              onKeyDown={(e) => {
                numericKeyDown(e);
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              onPaste={numericOnPaste}
              inputMode="decimal"
              pattern="[0-9]*[.,]?[0-9]*"
              className="w-full p-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded"
              min="0"
              step="1"
              placeholder="100"
            />
          </div>
        </div>

        {tokenSuggestionsVisible && (
          <div ref={listContainerRef} className="border rounded-md dark:border-gray-700 max-h-72 overflow-y-auto mb-4">
            {positionsLoading && filteredResults.length === 0 ? (
              <div className="p-3 text-gray-500 dark:text-gray-400">Loading...</div>
            ) : filteredResults.length === 0 ? (
              <div className="p-3 text-gray-500 dark:text-gray-400">{searchTerm ? 'No tokens found' : 'Type to search for tokens'}</div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredResults.map((r, idx) => (
                  <li
                    key={r.id}
                    data-idx={idx}
                    className={`p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${idx === selectedIndex ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
                    onMouseDown={() => handleSelectByMouse(r)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-gray-800 dark:text-gray-200">
                        <span className="font-medium">{r.name}</span> <span className="text-gray-500 dark:text-gray-400">({r.ticker})</span>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {r.hasPosition ? (
                            <>Existing position: {formatCurrency(r.holdingsValue)} · {r.holdingsAmount} {r.ticker} · ROI: {r.roi}</>
                          ) : (
                            r.marketCapRank ? <>Market cap rank: {r.marketCapRank}</> : null
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mb-4">
          <AllocationLocationSelector
            locations={locations}
            selectedLocationId={targetLocationId}
            onSelect={(locationId) => setTargetLocationId(locationId || '')}
            onCreate={onCreateTargetLocation}
            onRename={onRenameTargetLocation}
            onDelete={onDeleteTargetLocation}
            hideResults={tokenSuggestionsVisible}
            tokenTicker={selectedToken?.ticker || ''}
            tokenLocationBalances={selectedToken?.locationBalances || []}
          />
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedToken || parseFloatFromString(amount) < 0 || Number.isNaN(parseFloatFromString(amount))}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTokenModal;


